import { useEffect } from 'react';
import useMarketSettingsStore from 'src/stores/useMarketSettingsStore';
import useDriftAccountStore from 'src/stores/useDriftAccountsStore';
import {
	UIMarket,
	convertMarginRatioToLeverage,
} from '@velocity-exchange/common';

/**
 * Syncs the on-chain max leverage of user's `PerpPosition` data to the local persisted store.
 * For "empty" PerpPositions (i.e. zero base size) with no open orders, we prioritize the saved leverage from the store (if any).
 */
function useSyncMarketSettings() {
	const savedLeverages = useMarketSettingsStore((s) => s.marketLeverages);
	const updateMaxLeverageForMarket = useMarketSettingsStore(
		(s) => s.setMaxLeverageForMarket
	);

	const currentUserKey = useDriftAccountStore((s) => s.currentUserKey);
	const currentAccount = useDriftAccountStore(
		(s) => s.accounts[currentUserKey ?? '']
	);

	useEffect(() => {
		if (!currentAccount?.client) return;

		const alreadySyncedMarketIndices = new Set<number>(); // a same market position could exist multiple times in the user's account - e.g. never used before perp position data are defaulted to market index 0

		// user must have an open position or open order for this market, in order to sync the max leverage with the store
		// for perp positions that are not open (i.e. zero base size) or not open orders, we prioritize the saved leverage from the store (if any)
		currentAccount.client.getActivePerpPositions().forEach((position) => {
			if (alreadySyncedMarketIndices.has(position.marketIndex)) return;

			const marketSymbol = UIMarket.createPerpMarket(position.marketIndex)
				.uiSymbols.marketSymbol;
			const onChainLeverage = convertMarginRatioToLeverage(
				position.maxMarginRatio,
				2
			);
			const savedLeverage = savedLeverages.find(
				(lev) => lev.marketSymbol === marketSymbol
			)?.positionMaxLeverage;

			if (onChainLeverage && savedLeverage !== onChainLeverage) {
				updateMaxLeverageForMarket(marketSymbol, onChainLeverage);
			}

			alreadySyncedMarketIndices.add(position.marketIndex);
		});
	}, [currentAccount, savedLeverages, updateMaxLeverageForMarket]);
}

export default useSyncMarketSettings;
